import { useState } from "react"
import type { ChangeEvent } from "react"
import BracketAllTrees from "@/components/bracket/BracketAllTrees"
import BracketTree from "@/components/bracket/BracketTree"
import EmptyMessage from "@/components/common/EmptyMessage"
import Loading from "@/components/common/Loading"
import StatusBadge from "@/components/common/StatusBadge"
import WideDialog from "@/components/common/WideDialog"
import { useAdminBracket } from "@/domain/admin/hooks/AdminBracketHook"
import { useExcelExport } from "@/domain/excel/hooks/ExcelExportHook"
import { ExcelKindPayload } from "@bracket/idl/main/excel/payload/ExcelPayloadPt"
import AdminBracketSizeField from "@/components/admin/AdminBracketSizeField"
import type { AdminDrawTarget } from "@/domain/admin/client/AdminBracketClient"

/**
 * 관리자 대진 편성 화면.
 *
 * 체급별 편성 대상을 보고 편성한다. 편성 · 재편성 · 전체 편성은 모두 확인 모달을 띄우고
 * **편성 규모는 그 모달 안에서 고른다**(누른 버튼 곁에서 정해야 어떤 규모로 뽑히는지 보인다).
 * 이미 편성된 체급은 같은 모달이 재편성 경고를 함께 보여준다 — 기존 대진을 지우고 새로 뽑기 때문이다.
 *
 * **편성은 곧 공개가 아니다** — 편성한 대진표는 비공개고, 「전체 대진표 공개」를 켜야
 * 공개 대진표 화면에 나간다. 공개는 **대회 단위**다(체급별로 조금씩 내보내지 않는다).
 * 대회 전에 대진을 짜는 것까지가 이 화면의 일이고, 경기 진행 · 결과는 다루지 않는다.
 *
 * 체급 하나씩 「보기」로 열어 보거나, 「전체 대진표」로 모든 체급을 한 화면에서 훑을 수 있다.
 * 둘 다 목록 아래에 펼치지 않고 모달로 띄운다 — 카드 목록이 길면 아래로 한참 내려가야 보였다.
 * 편성 · 재편성 직후에도 그 결과를 같은 모달로 보여준다.
 */
export default function AdminBracketPage() {
  const {
    tournaments,
    tournamentId,
    targets,
    detail,
    drawAllResult,
    allBrackets,
    isAllShown,
    isBracketPublic,
    isBracketDrawn,
    size,
    isLoaded,
    isSaving,
    errorMessage,
    setSize,
    resetSize,
    selectTournament,
    draw,
    drawAll,
    openDetail,
    closeDetail,
    openAllBrackets,
    closeAllBrackets,
    updateVisibility,
    remove,
  } = useAdminBracket()

  const [drawTarget, setDrawTarget] = useState<AdminDrawTarget | null>(null)
  const [isDrawAllOpen, setIsDrawAllOpen] = useState(false)
  const [isDrawAllRedraw, setIsDrawAllRedraw] = useState(false)

  const undrawnTargets = targets.filter((target) => !target.isDrawn)
  const drawnTargetCount = targets.length - undrawnTargets.length
  const drawAllTargetCount = isDrawAllRedraw
    ? targets.length
    : undrawnTargets.length
  const drawAllGuide = toDrawAllGuide()
  const {
    isExporting,
    errorMessage: excelErrorMessage,
    exportExcel,
  } = useExcelExport()

  function onChangeTournament(event: ChangeEvent<HTMLSelectElement>) {
    void selectTournament(event.target.value)
  }

  /**
   * 체급 편성 — 규모를 고르도록 확인 모달을 띄운다
   *
   * 규모는 열 때마다 자동으로 되돌린다(앞서 고른 값이 다른 체급에 몰래 이어지지 않게).
   * 이미 편성된 체급이면 같은 모달이 재편성 경고를 함께 보여준다.
   */
  function onClickDraw(target: AdminDrawTarget) {
    resetSize()
    setDrawTarget(target)
  }

  function onClickCloseDraw() {
    setDrawTarget(null)
  }

  function onClickConfirmDraw() {
    if (!drawTarget) return

    void draw(drawTarget, drawTarget.isDrawn).then(() => setDrawTarget(null))
  }

  /**
   * 뽑을 것이 없을 때의 안내 문구 (뽑을 것이 있으면 빈 문자열)
   *
   * 버튼을 막아 두면 왜 못 누르는지 알 수 없어 대화상자를 열어 이유를 말한다.
   */
  function toDrawAllGuide(): string {
    if (targets.length === 0) {
      return "편성할 수 있는 체급이 없습니다. 편성 대상은 승인 완료 + 체급 확정 선수만 잡히니, 참가 신청 관리에서 선수 승인과 체급 확정을 먼저 진행해 주세요."
    }

    if (drawAllTargetCount === 0) {
      return "이 대회는 모든 체급이 이미 편성돼 있습니다. 다시 뽑으려면 아래 재편성을 켜 주세요."
    }

    return ""
  }

  /** 전체 편성 — 편성 규모는 모달 안에서 고른다 (모든 체급에 같은 규모를 쓴다) */
  function onClickOpenDrawAll() {
    resetSize()
    setIsDrawAllRedraw(false)
    setIsDrawAllOpen(true)
  }

  function onClickCloseDrawAll() {
    setIsDrawAllOpen(false)
  }

  function onChangeDrawAllRedraw(event: ChangeEvent<HTMLInputElement>) {
    setIsDrawAllRedraw(event.target.checked)
  }

  function onClickConfirmDrawAll() {
    void drawAll(isDrawAllRedraw).then(() => setIsDrawAllOpen(false))
  }

  /** 편성한 모든 체급을 모달 하나에 펼쳐 훑는다 */
  function onClickOpenAll() {
    void openAllBrackets()
  }

  function onCloseAll() {
    closeAllBrackets()
  }

  function onCloseDetail() {
    closeDetail()
  }

  /** 공개는 대회 단위다 — 체급별로 조금씩 내보내지 않는다 */
  function onClickToggleVisibility() {
    void updateVisibility(!isBracketPublic)
  }

  function onClickExportExcel() {
    void exportExcel(ExcelKindPayload.EXCEL_KIND_BRACKET, tournamentId)
  }

  return (
    <div className="app-page-admin">
      <section className="app-page-head flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="app-txt-eyebrow text-app-primary-600">BRACKETS</p>
          <h2 className="app-txt-h1 mt-2">대진 편성</h2>
          <p className="app-txt-body2 mt-2 text-app-text-200">
            승인 완료 + 체급 확정 선수만 편성 대상입니다. 같은 체육관 선수는
            1회전에서 만나지 않게 배치하고, 인원이 2의 거듭제곱이 아니면
            부전승이 생깁니다.
          </p>
          <p className="app-txt-body2 mt-1 text-app-text-200">
            편성한 대진표는 <strong>비공개</strong>입니다. 「전체 대진표
            공개」를 눌러야 참가자에게 보입니다 — 언제든 다시 숨길 수 있습니다.
          </p>
        </div>

        <div className="flex shrink-0 flex-col gap-1.5 md:items-end">
          {/* 버튼 라벨이 길어 모바일 폭에서는 한 줄에 들어가지 않는다 — 줄을 넘겨 받는다 */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onClickOpenDrawAll}
              disabled={isSaving || !tournamentId}
              className="focus-ring app-btn app-btn-primary"
            >
              전체 대진표 짜기
            </button>
            <button
              type="button"
              onClick={onClickOpenAll}
              disabled={!tournamentId}
              className="focus-ring app-btn app-btn-outline"
            >
              전체 대진표
            </button>
            <button
              type="button"
              onClick={onClickToggleVisibility}
              disabled={isSaving || !isBracketDrawn}
              className="focus-ring app-btn app-btn-brand"
            >
              {isBracketPublic ? "전체 대진표 숨기기" : "전체 대진표 공개"}
            </button>
            <button
              type="button"
              onClick={onClickExportExcel}
              disabled={isExporting || !tournamentId}
              className="focus-ring app-btn app-btn-primary"
            >
              {isExporting ? "엑셀 만드는 중…" : "대진표 엑셀"}
            </button>
          </div>
          <p className="app-txt-caption text-app-text-200">
            편성된 모든 경기를 경기 번호 순으로 한 장에 뽑습니다.
          </p>
          {excelErrorMessage && (
            <p
              role="alert"
              className="app-txt-caption text-app-red-300"
            >
              {excelErrorMessage}
            </p>
          )}
        </div>
      </section>

      <div className="app-card mb-5 flex flex-wrap items-end gap-3">
        {/* 대회명이 길어 select 의 최소 폭이 모바일 폭을 넘는다 — 줄 폭에 맞춰 줄인다 */}
        <div className="flex w-full min-w-0 flex-col gap-1.5 sm:w-auto">
          <label
            htmlFor="admin-bracket-tournament"
            className="app-label-sub"
          >
            대회
          </label>
          <select
            id="admin-bracket-tournament"
            value={tournamentId}
            onChange={onChangeTournament}
            className="focus-ring app-field w-full min-w-0 sm:w-72"
          >
            <option value="">대회를 선택하세요</option>
            {tournaments.map((tournament) => (
              <option
                key={tournament.id}
                value={tournament.id}
              >
                {tournament.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {errorMessage && (
        <p
          role="alert"
          className="app-alert app-alert-error mb-5"
        >
          {errorMessage}
        </p>
      )}

      {drawAllResult && (
        <div
          role="status"
          className={`app-alert mb-5 ${drawAllResult.failures.length > 0 ? "app-alert-brand" : "app-alert-success"}`}
        >
          <p>
            체급 {drawAllResult.drawnCount}개를 편성했습니다.
            {drawAllResult.skippedCount > 0 &&
              ` 이미 편성돼 있던 ${drawAllResult.skippedCount}개는 그대로 두었습니다.`}
          </p>
          {drawAllResult.failures.length > 0 && (
            <>
              <p className="mt-2 font-bold">
                편성하지 못한 체급 {drawAllResult.failures.length}개
              </p>
              <ul className="app-txt-caption mt-1 list-disc pl-5">
                {drawAllResult.failures.map((failure) => (
                  <li key={failure.key}>
                    {failure.categoryLabel} {failure.weightClassName} —{" "}
                    {failure.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {!isLoaded && <Loading label="편성 대상을 불러오는 중" />}

      {isLoaded && tournamentId && targets.length === 0 && (
        <EmptyMessage message="편성할 수 있는 체급이 없습니다. 선수 승인과 체급 확정을 먼저 진행해 주세요." />
      )}

      {targets.length > 0 && (
        <ul className="app-grid-hairline list-none md:grid-cols-2 xl:grid-cols-3">
          {targets.map((target) => (
            <li
              key={target.key}
              className="flex flex-col"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="app-txt-h3">{target.weightClassName}</h3>
                {target.isDrawn && (
                  <StatusBadge
                    label={target.bracketStatusLabel}
                    className="shrink-0"
                  />
                )}
              </div>

              <p className="app-txt-body2 mt-2 text-app-text-200">
                {target.sportLabel} · {target.divisionLabel} ·{" "}
                {target.genderLabel}
              </p>
              <p className="app-txt-body2 mt-1 text-app-text-200">
                편성 대상 {target.playerCount}명
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onClickDraw(target)}
                  disabled={isSaving}
                  className="focus-ring app-btn app-btn-primary flex-1"
                >
                  {target.isDrawn ? "재편성" : "편성"}
                </button>

                {target.isDrawn && (
                  <>
                    <button
                      type="button"
                      onClick={() => openDetail(target.bracketId)}
                      className="focus-ring app-btn app-btn-outline"
                    >
                      보기
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(target.bracketId)}
                      disabled={isSaving}
                      className="focus-ring app-btn app-btn-danger-outline"
                    >
                      삭제
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {isAllShown && (
        <WideDialog
          titleId="all-brackets-title"
          title={`전체 대진표 · 체급 ${allBrackets.length}개`}
          description="편성한 모든 체급을 체급 순으로 펼쳐 놓았습니다. 경기 번호는 대회 전체 순서입니다."
          onClose={onCloseAll}
        >
          <BracketAllTrees brackets={allBrackets} />
        </WideDialog>
      )}

      {detail && (
        <WideDialog
          titleId="bracket-detail-title"
          title={`${detail.weightClassName} ${detail.sizeLabel}`}
          description={`${detail.sportLabel} · ${detail.divisionLabel} · ${detail.genderLabel} · 슬롯 ${detail.slotCount}`}
          onClose={onCloseDetail}
        >
          <p
            className={`app-alert ${
              detail.isPublic ? "app-alert-success" : "app-alert-brand"
            }`}
          >
            {detail.isPublic
              ? "공개 중입니다. 참가자에게 이 대진표가 보입니다."
              : "비공개입니다. 상단의 「전체 대진표 공개」를 눌러야 참가자에게 보입니다."}
          </p>

          <div className="mt-5 overflow-x-auto pb-4">
            <BracketTree rounds={detail.rounds} />
          </div>

          <p className="app-txt-caption mt-3 text-app-text-200">
            1회전만 선수가 채워집니다. 다음 단계는 대진표 골격이고, 부전승
            자리만 미리 올라갑니다 — 경기 결과는 이 시스템에서 다루지 않습니다.
          </p>
        </WideDialog>
      )}

      {isDrawAllOpen && (
        <div className="app-dialog-backdrop">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="draw-all-title"
            className="app-dialog"
          >
            <h3
              id="draw-all-title"
              className="app-txt-h3"
            >
              전체 대진표를 짤까요?
            </h3>
            {drawAllGuide ? (
              <p className="app-txt-body2 mt-3 leading-6 text-app-text-300">
                {drawAllGuide}
              </p>
            ) : (
              <p className="app-txt-body2 mt-3 leading-6 text-app-text-300">
                체급 <strong>{drawAllTargetCount}개</strong>를 아래 규모로
                편성합니다. 선수가 1명뿐인 체급도 상대 자리를 비운 부전승으로
                편성됩니다.
              </p>
            )}
            {targets.length > 0 && (
              <AdminBracketSizeField
                id="draw-all-size"
                value={size}
                onChange={setSize}
              />
            )}
            {drawnTargetCount > 0 && (
              <label className="app-txt-body2 mt-4 flex items-start gap-2 text-app-text-300">
                <input
                  type="checkbox"
                  checked={isDrawAllRedraw}
                  onChange={onChangeDrawAllRedraw}
                  className="focus-ring mt-1"
                />
                <span>
                  이미 편성된 체급 {drawnTargetCount}개도 다시 뽑기 — 기존
                  대진이 사라지고 새로 뽑습니다.
                </span>
              </label>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClickCloseDrawAll}
                className="focus-ring app-btn app-btn-outline"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={onClickConfirmDrawAll}
                disabled={isSaving || drawAllTargetCount === 0}
                className="focus-ring app-btn app-btn-primary"
              >
                {isSaving ? "편성하는 중…" : "전체 편성"}
              </button>
            </div>
          </div>
        </div>
      )}

      {drawTarget && (
        <div className="app-dialog-backdrop">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="draw-title"
            className="app-dialog"
          >
            <h3
              id="draw-title"
              className="app-txt-h3"
            >
              {drawTarget.isDrawn ? "다시 편성할까요?" : "편성할까요?"}
            </h3>
            <p className="app-txt-body2 mt-3 leading-6 text-app-text-300">
              {drawTarget.isDrawn
                ? `「${drawTarget.weightClassName}」의 기존 대진이 사라지고 새로 뽑습니다. 이 작업은 되돌릴 수 없습니다.`
                : `「${drawTarget.weightClassName}」 선수 ${drawTarget.playerCount}명을 아래 규모로 편성합니다.`}
            </p>
            <AdminBracketSizeField
              id="draw-size"
              value={size}
              onChange={setSize}
            />

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClickCloseDraw}
                className="focus-ring app-btn app-btn-outline"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={onClickConfirmDraw}
                disabled={isSaving}
                className="focus-ring app-btn app-btn-primary"
              >
                {drawTarget.isDrawn ? "재편성" : "편성"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
